import { ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Feature, Testimonial, FAQType } from './types';

// Reusable feature card component with improved styling and padding
export const FeatureCard = ({ feature }: { feature: Feature }) => {
    // Vibrant gradient backgrounds based on feature color
    const getGradient = (color: string) => {
        switch(color) {
            case 'primary':
                return 'from-violet-500/20 to-fuchsia-500/10';
            case 'secondary':
                return 'from-blue-500/20 to-cyan-400/10';
            case 'accent':
                return 'from-pink-500/20 to-rose-400/10';
            case 'success':
                return 'from-emerald-500/20 to-green-400/10';
            case 'warning':
                return 'from-amber-500/20 to-yellow-400/10';
            case 'destructive':
                return 'from-red-500/20 to-orange-400/10';
            default:
                return 'from-indigo-500/20 to-purple-400/10';
        }
    };
    
    const getIconColor = (color: string) => {
        switch(color) {
            case 'primary': return 'text-violet-600 dark:text-violet-400';
            case 'secondary': return 'text-blue-600 dark:text-blue-400';
            case 'accent': return 'text-pink-600 dark:text-pink-400';
            case 'success': return 'text-emerald-600 dark:text-emerald-400';
            case 'warning': return 'text-amber-600 dark:text-amber-400';
            case 'destructive': return 'text-red-600 dark:text-red-400';
            default: return 'text-indigo-600 dark:text-indigo-400';
        }
    };
    
    const gradientBg = `bg-gradient-to-br ${getGradient(feature.color)}`;
    const iconColor = getIconColor(feature.color);
    
    return (
        <Card className="card-baby border border-primary/5 hover:border-primary/20 transition-all duration-300 h-full bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl group hover:-translate-y-1">
            <CardHeader className="p-6 pb-2">
                <div className={`h-16 w-16 rounded-full ${gradientBg} flex items-center justify-center mb-4 shadow-md group-hover:shadow-lg`}>
                    <div className={iconColor}>{feature.icon}</div>
                </div>
                <CardTitle className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">{feature.title}</CardTitle>
                <CardDescription className="text-gray-600 dark:text-gray-300">{feature.description}</CardDescription>
            </CardHeader>
            <CardContent className="px-6 pb-2">
                <p className="text-sm text-gray-600 dark:text-gray-300">{feature.content}</p>
            </CardContent>
            <CardFooter className="p-6 pt-2">
                <Button variant="ghost" size="sm" className={`gap-1 hover:gap-2 -ml-2 px-2 transition-all duration-300 ${iconColor}`}>
                    Learn more <ArrowRight className="h-4 w-4 transition-all duration-300" />
                </Button>
            </CardFooter>
        </Card>
    );
};

// Testimonial card component with enhanced padding and vibrant colors
export const TestimonialCard = ({ testimonial }: { testimonial: Testimonial }) => (
    <Card className="card-baby border border-primary/5 bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
        <CardContent className="pt-8 px-6 pb-6">
            <div className="bg-gradient-to-r from-amber-400 to-yellow-300 h-1 w-16 rounded-full mb-6 mx-auto"></div>
            <div className="flex mb-4 justify-center">
                {[...Array(testimonial.rating)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-amber-500 dark:text-amber-400 fill-current" viewBox="0 0 24 24">
                        <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" />
                    </svg>
                ))}
            </div>
            <p className="text-gray-700 dark:text-gray-200 italic mb-6 text-center">"‎{testimonial.quote}"</p>
            <div className="h-px w-16 bg-gradient-to-r from-violet-400 to-fuchsia-400 mx-auto mb-4"></div>
            <p className="text-fuchsia-600 dark:text-fuchsia-400 font-semibold text-center">{testimonial.author}</p>
        </CardContent>
    </Card>
);

// FAQ item component with proper padding and vibrant hover effects
export const FaqItem = ({ faq }: { faq: FAQType }) => (
    <Collapsible className="border-b border-violet-200 dark:border-violet-800/30 py-5 group">
        <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors duration-200 pr-4">{faq.question}</h3>
            <CollapsibleTrigger className="rounded-full p-2 hover:bg-violet-100 dark:hover:bg-violet-800/30 transition-all duration-200">
                <ArrowRight className="h-5 w-5 text-violet-500 dark:text-violet-400 group-hover:rotate-90 transform transition-all duration-300" />
            </CollapsibleTrigger>
        </div>
        <CollapsibleContent className="pt-3 pr-10 pb-2 animate-slideDown">
            <p className="text-gray-600 dark:text-gray-300">{faq.answer}</p>
        </CollapsibleContent>
    </Collapsible>
);

// Section header with badge - vibrant colors
export const SectionHeader = ({ 
    badgeText, 
    badgeClasses,
    title, 
    description
}: { 
    badgeText: string, 
    badgeClasses: string,
    title: string, 
    description: string 
}) => (
    <div className="text-center mb-16">
        <Badge className={`mb-4 ${badgeClasses} font-medium px-4 py-2`}>
            {badgeText}
        </Badge>
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-800 dark:text-white bg-clip-text bg-gradient-to-r from-violet-600 to-fuchsia-600 dark:from-violet-400 dark:to-fuchsia-400">
            {title}
        </h2>
        <p className="text-gray-600 dark:text-gray-300 text-lg max-w-2xl mx-auto">
            {description}
        </p>
    </div>
);